const ROOT_SELECTOR = '[data-goodlot-extra-discount]';
const APPLIED_CODE_STORAGE_KEY = 'goodlot-applied-discount-code';

function initGoodLotExtraDiscount(root) {
  if (!root || root.dataset.goodlotExtraDiscountReady === 'true') return;

  root.dataset.goodlotExtraDiscountReady = 'true';

  const actionButton = root.querySelector('[data-goodlot-discount-open]');

  if (!actionButton) return;

  const discountCode =
    root.dataset.discountCode ||
    'GOODLOT10';

  let actionBusy = false;

  /*
   * The old badge opened a discount modal.
   * The badge is now a direct "discount + add to cart" action.
   *
   * These changes are accessibility-only and do not change the badge UI.
   */
  actionButton.removeAttribute('aria-haspopup');
  actionButton.removeAttribute('aria-controls');
  actionButton.removeAttribute('aria-expanded');
  actionButton.setAttribute(
    'aria-label',
    'Apply first order discount and add to cart'
  );

  const applyDiscountToCart = async (code) => {
    const routeRoot =
      window.Shopify?.routes?.root ||
      '/';

    const response = await fetch(`${routeRoot}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        discount: code,
      }),
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(
        `Unable to apply discount. Status: ${response.status}`
      );
    }

    return response.json();
  };

  const rememberAppliedCode = (code) => {
    try {
      window.sessionStorage.setItem(
        APPLIED_CODE_STORAGE_KEY,
        code
      );
    } catch (error) {
      // Storage is optional.
    }
  };

  const reapplyRememberedDiscount = async () => {
    let rememberedCode = '';

    try {
      rememberedCode =
        window.sessionStorage.getItem(
          APPLIED_CODE_STORAGE_KEY
        ) || '';
    } catch (error) {
      rememberedCode = '';
    }

    if (!rememberedCode) return;

    try {
      await applyDiscountToCart(rememberedCode);
    } catch (error) {
      console.warn(
        'GoodLot: unable to reapply saved discount code.',
        error
      );
    }
  };

  /*
   * Find the REAL GoodLot PDP product form.
   *
   * We submit this existing form instead of creating a second
   * cart/add implementation. That means the current GoodLot
   * product-form behavior, cart events, cart animation, and
   * cart drawer opening flow remain in control.
   */
  const getProductFormElements = () => {
    const pdp =
      root.closest('[data-goodlot-product-main]') ||
      document;

    const component =
      pdp.querySelector(
        'product-form-component.goodlot-pdp__product-form'
      ) ||
      pdp.querySelector('product-form-component');

    const form =
      component?.querySelector(
        'form[data-type="add-to-cart-form"]'
      ) ||
      component?.querySelector('form');

    const submitButton =
      form?.querySelector(
        '.goodlot-pdp__add[type="submit"]:not([disabled])'
      ) ||
      form?.querySelector(
        'button[type="submit"]:not([disabled])'
      ) ||
      null;

    return {
      component,
      form,
      submitButton,
    };
  };

  /*
   * Celebration is created entirely in JS.
   *
   * No permanent markup or CSS is added, so the existing
   * Extra Discount badge UI is not changed.
   */
  const playCelebration = () => {
    if (
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)'
      ).matches
    ) {
      return;
    }

    actionButton.animate(
      [
        {
          transform: 'scale(1)',
          filter: 'brightness(1)',
        },
        {
          transform: 'scale(.985)',
          filter: 'brightness(1.03)',
          offset: 0.22,
        },
        {
          transform: 'scale(1.018)',
          filter:
            'brightness(1.08) saturate(1.08)',
          offset: 0.52,
        },
        {
          transform: 'scale(1)',
          filter: 'brightness(1)',
        },
      ],
      {
        duration: 720,
        easing: 'cubic-bezier(.22, 1, .36, 1)',
      }
    );

    const rect =
      actionButton.getBoundingClientRect();

    const layer =
      document.createElement('div');

    Object.assign(layer.style, {
      position: 'fixed',
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      zIndex: '2147483000',
      pointerEvents: 'none',
      overflow: 'visible',
    });

    document.body.appendChild(layer);

    const particles = [
      {
        text: '🎉',
        x: -0.34,
        y: -1.08,
        size: 22,
        rotate: -34,
        delay: 0,
      },
      {
        text: '✨',
        x: 0.34,
        y: -1.10,
        size: 19,
        rotate: 34,
        delay: 32,
      },
      {
        text: '🎊',
        x: 0.10,
        y: -1.23,
        size: 17,
        rotate: 78,
        delay: 58,
      },
      {
        text: '✦',
        x: -0.45,
        y: -0.20,
        size: 15,
        rotate: -64,
        delay: 70,
      },
      {
        text: '✦',
        x: 0.45,
        y: -0.08,
        size: 15,
        rotate: 52,
        delay: 94,
      },
      {
        text: '◆',
        x: -0.24,
        y: 0.82,
        size: 11,
        rotate: 92,
        delay: 46,
      },
      {
        text: '●',
        x: 0.30,
        y: 0.78,
        size: 10,
        rotate: 18,
        delay: 82,
      },
      {
        text: '★',
        x: 0.46,
        y: 0.52,
        size: 13,
        rotate: 58,
        delay: 24,
      },
    ];

    const animations =
      particles.map((particle) => {
        const node =
          document.createElement('span');

        node.textContent = particle.text;

        Object.assign(node.style, {
          position: 'absolute',
          left: '50%',
          top: '50%',
          display: 'block',
          margin: '0',
          padding: '0',
          fontSize: `${particle.size}px`,
          lineHeight: '1',
          opacity: '0',
          transform:
            'translate(-50%, -50%) scale(.35)',
          transformOrigin: 'center',
          willChange: 'transform, opacity',
        });

        layer.appendChild(node);

        const x =
          rect.width * particle.x;

        const y =
          rect.height * particle.y;

        const animation =
          node.animate(
            [
              {
                opacity: 0,
                transform:
                  'translate(-50%, -50%) ' +
                  'scale(.35) rotate(0deg)',
              },
              {
                opacity: 1,
                offset: 0.14,
              },
              {
                opacity: 0,
                transform:
                  `translate(` +
                  `calc(-50% + ${x}px), ` +
                  `calc(-50% + ${y}px)` +
                  `) scale(1) ` +
                  `rotate(${particle.rotate}deg)`,
              },
            ],
            {
              duration: 900,
              delay: particle.delay,
              easing:
                'cubic-bezier(.16, .84, .44, 1)',
              fill: 'forwards',
            }
          );

        return animation.finished.catch(
          () => {}
        );
      });

    Promise.allSettled(
      animations
    ).finally(() => {
      layer.remove();
    });
  };

  const submitExistingProductForm = () => {
    const {
      form,
      submitButton,
    } = getProductFormElements();

    if (!form || !submitButton) {
      throw new Error(
        'GoodLot PDP Add to cart form is unavailable.'
      );
    }

    /*
     * requestSubmit() fires the existing native submit event.
     * The current product-form-component remains responsible
     * for the actual cart add, fly-to-cart/cart events, and
     * cart drawer opening.
     */
    if (
      typeof form.requestSubmit === 'function'
    ) {
      form.requestSubmit(submitButton);
      return;
    }

    /*
     * Older-browser fallback.
     * This is only used if requestSubmit() is unavailable.
     */
    submitButton.click();
  };

  const handleBadgeAction = async () => {
    if (actionBusy) return;

    const {
      form,
      submitButton,
    } = getProductFormElements();

    if (!form || !submitButton) {
      console.warn(
        'GoodLot: Add to cart form/button could not be found.'
      );
      return;
    }

    actionBusy = true;
    actionButton.setAttribute(
      'aria-busy',
      'true'
    );

    /*
     * Animation starts immediately so the interaction feels instant.
     * The discount request happens at the same time.
     */
    playCelebration();

    try {
      await applyDiscountToCart(
        discountCode
      );

      rememberAppliedCode(
        discountCode
      );

      /*
       * Immediately submit the existing PDP product form.
       * No modal. No simulated Extra Discount popup.
       * No custom cart drawer implementation.
       */
      submitExistingProductForm();
    } catch (error) {
      console.error(
        'GoodLot: discount could not be applied.',
        error
      );
    } finally {
      /*
       * Prevent accidental double taps during the current
       * add-to-cart / cart-drawer sequence.
       */
      window.setTimeout(() => {
        actionBusy = false;
        actionButton.removeAttribute(
          'aria-busy'
        );
      }, 1200);
    }
  };

  actionButton.addEventListener(
    'click',
    (event) => {
      event.preventDefault();
      handleBadgeAction();
    }
  );

  /*
   * Preserve the current behavior where an already-applied
   * GoodLot code remains active during the same session.
   */
  reapplyRememberedDiscount();
}

function initAllGoodLotExtraDiscount(
  scope = document
) {
  scope
    .querySelectorAll(ROOT_SELECTOR)
    .forEach((root) => {
      initGoodLotExtraDiscount(root);
    });
}

if (
  document.readyState === 'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    () =>
      initAllGoodLotExtraDiscount(
        document
      ),
    {
      once: true,
    }
  );
} else {
  initAllGoodLotExtraDiscount(
    document
  );
}

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initAllGoodLotExtraDiscount(
      event.target
    );
  }
);